import { FunctionComponent, ReactElement, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BaseIcon } from '../../../components/DesignSystem';
import { store } from '../../../store/store';
import { FormText } from '../../../styles/TypoStyle';
import { Insurance, insuranceByCode, InsuranceModel } from '../../../utils/InsuranceModel';
import { ScoreProps } from '../../../utils/ResponsePageModel';

const Container = styled.div`
  base-card {
    margin: 0px;
  }
  width: 100%;
`;
const CardTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin: 25px 16px;
  @media (max-width: 496px) {
    margin: 25px;
  }
`;
const CardTitle = styled(FormText)`
  font-weight: bold;
  color: var(--colors-teal-dark);
  margin-top: 8px;
  //mobile
  @media (max-width: 496px) {
    font-size: var(--typo-form3-font-size);
    line-height: var(--typo-form3-line-height));
  }
`;

const MainContainer = styled.div`
  border-left: 2px solid green;
  padding: 0 20px 10px;
  @media (max-width: 496px) {
    border: none;
    padding: 0 30px 30px;
  }
`;
const Example: FunctionComponent<ScoreProps> = ({ score }): ReactElement => {
  const [asm, setAsm] = useState<InsuranceModel>();
  const {
    state: { responseInfo },
  } = useContext(store);
  useEffect(() => {
    if (score?.holderAsmCode && score?.holderAsmCode !== undefined && score?.holderAsmCode !== '0')
      setAsm(insuranceByCode(score?.holderAsmCode as unknown as Insurance));
  }, [score]);
  return (
    <>
      {responseInfo?.responsePageType !== 'CONSO' && (
        <Container>
          <CardTitleContainer>
            <BaseIcon icon="info-fill" big="true" />
            <CardTitle>Un exemple pour mieux comprendre cette offre :</CardTitle>
          </CardTitleContainer>
          <MainContainer>
            <small>
              Exemple pour des taux de regroupement de crédits effectué sous forme de prêt immobilier en vigueur au
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_date_vigueur + ' '}
              et susceptibles de variations.{' '}
            </small>
            <small>
              Pour un prêt de regroupement de crédits effectué sous forme de prêt immobilier amortissable de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_projet_montant + ' '}, garanti par une
              hypothèque, décaissé en une seule fois, d’une durée de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_projet_duree + ' '}
              au taux débiteur fixe de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_projet_tnc + ' '}, le remboursement
              s’effectue en
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_mensualites_total + ' '}
              (hors assurance facultative) se décomposant comme suit :{' '}
            </small>
            <small>
              <b>les deux premières échéances de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_mensualites_2 + ' '}</b>
              et
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_mensualites_suivantes + ' '}</b>
              <b>d’un montant de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_mensualites_montant + ' '}</b>.{' '}
              <b>Taux Annuel Effectif Global (TAEG) fixe de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_mensualites_taeg + ' '}</b>.{' '}
            </small>
            <small>
              Le coût total du crédit est de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_cout_total + ' '} dont
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_cout_interets + ' '} d’intérêts,
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_frais_acte + ' '} de frais d’acte,
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_frais_compte + ' '} de frais de tenue de
              compte et {' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_frais_dossier + ' '}
              de frais de dossier.
              <b> Le montant total dû par l’emprunteur est de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_immo_montant_total + ' '}</b>.
            </small>
          </MainContainer>
        </Container>
      )}
      {responseInfo?.responsePageType !== 'IMMO' && (
        <Container>
          <CardTitleContainer>
            <BaseIcon icon="info-fill" big="true" />
            <CardTitle>Un exemple pour mieux comprendre cette offre :</CardTitle>
          </CardTitleContainer>
          <MainContainer>
            <small>Sandrine et Jérôme souhaitent regrouper tous leurs crédits à la consommation. </small>
            <small>
              Pour leur projet, ils souhaitent emprunter
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_projet_montant + ' '} remboursables en
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_projet_duree + ' '} (*) mensualités</b>
              <b> de {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_projet_mensualite + ' '}</b>
              (hors assurance facultative) pour un <b>montant total dû de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_montant_total + ' '}</b>
              (hors assurance facultative), dont
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_cout_interets + ' '} d’intérêts et des
              frais de dossier capitalisés dans le montant emprunté de
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_frais_dossier + ' '}</b>. Soit un
              <b> Taux Annuel Effectif Global (TAEG) fixe de</b>
              <b>{' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_mensualites_taeg + ' '}</b> et un taux
              débiteur fixe de {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_mensualites_tnc + ' '}
              (taux en vigueur au {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_date_vigueur}).{' '}
            </small>
            <small>
              Pour se prémunir en cas de Décès , Invalidité (Perte Totale et Irréversible d’Autonomie), Maladie
              (Incapacité Temporaire Totale de Travail), ils ont chacun souscrit à l’assurance Emprunteurs DIM
              facultative. Le coût de cette assurance est de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_cout_assurance + ' '} par mois pour
              Sandrine et Jérôme qui ont moins de 55 ans et exercent une activité professionnelle, elle s’ajoute à la
              mensualité du crédit. L’assurance Emprunteurs est un produit assuré par Cardif Assurance Vie et Cardif
              Assurances Risques Divers. Le Taux Annuel Effectif de l’Assurance est de
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_taeg_assurance + ' '}, soit un coût total
              de l’assurance de {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_total_assurance + ' '}.
              <br />
              (*) Durée du contrat de crédit :
              {' ' + (window as unknown as WindowWithEnv)._env_.exemple_conso_duree_credit + ' '} à compter de la date
              de mise à disposition des fonds incluant une période de report total de paiement de 1 mois avec calcul
              d’intérêts.
            </small>
          </MainContainer>
        </Container>
      )}
    </>
  );
};

export default Example;
